import { useEffect, useState, useCallback, useRef } from "react";
import AppShell from "@/components/AppShell";
import StatusPill from "@/components/StatusPill";
import api, { formatApiError, API_BASE } from "@/lib/api";
import twilioDevice from "@/lib/twilioDevice";
import plivoDevice from "@/lib/plivoDevice";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Clock, PhoneIncoming, PhoneOutgoing, ArrowRight, Phone, Headphones, Stop, Broadcast, WhatsappLogo, ChatCircleText, UserPlus, PaperPlaneTilt } from "@phosphor-icons/react";
import OnboardingWizard from "@/components/OnboardingWizard";
import CallingUpsellCard from "@/components/CallingUpsellCard";

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtDur(s) {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    const sec = String(s % 60).padStart(2, "0");
    return `${m}:${sec}`;
}

export default function Dashboard() {
    const { user } = useAuth();
    const feat = user?.features || {};
    const callCenter = feat.call_center !== false;
    const waEnabled = !!feat.whatsapp;
    const [recent, setRecent] = useState([]);
    const [stats, setStats] = useState({ total_calls: 0, completed: 0, failed: 0, total_duration_sec: 0 });
    const [ov, setOv] = useState(null);
    const [liveCalls, setLiveCalls] = useState([]);
    const [monitoringId, setMonitoringId] = useState(null);
    const [monProvider, setMonProvider] = useState("twilio");
    const monDevice = monProvider === "plivo" ? plivoDevice : twilioDevice;
    const [showOnboarding, setShowOnboarding] = useState(false);
    const heartbeatRef = useRef(null);
    const monitoredCallRef = useRef(null);

    const stopHeartbeat = useCallback((notifyBackend = true) => {
        if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
        if (notifyBackend && monitoredCallRef.current) {
            api.post(`/calls/${monitoredCallRef.current}/monitor/stop`).catch(() => {});
        }
        monitoredCallRef.current = null;
    }, []);

    const refresh = useCallback(async () => {
        try {
            const [c, s] = await Promise.all([api.get("/calls?limit=12"), api.get("/calls/stats")]);
            setRecent(c.data);
            setStats(s.data);
        } catch (_e) {}
    }, []);

    useEffect(() => {
        if (user?.role !== "admin" || !callCenter) return;
        api.get("/settings")
            .then(({ data }) => { if (!data.onboarding_completed) setShowOnboarding(true); })
            .catch(() => {});
    }, [user, callCenter]);

    const isAdmin = user?.role === "admin" || user?.role === "supervisor";

    useEffect(() => {
        if (!isAdmin) return;
        api.get("/settings")
            .then(({ data }) => setMonProvider(data.active_provider || "twilio"))
            .catch(() => {});
    }, [isAdmin]);

    const refreshLive = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const { data } = await api.get("/calls/live");
            setLiveCalls(data);
        } catch (_e) {}
    }, [isAdmin]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        api.get("/overview").then(({ data }) => setOv(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        refreshLive();
        const t = setInterval(refreshLive, 4000);
        return () => { clearInterval(t); twilioDevice.stopMonitor(); plivoDevice.stopMonitor(); stopHeartbeat(); };
    }, [isAdmin, refreshLive, stopHeartbeat]);

    useEffect(() => {
        const handle = ({ event }) => {
            if (event === "monitor:end" || event === "monitor:error") {
                stopHeartbeat();
                setMonitoringId(null);
                if (event === "monitor:error") toast.error("Monitoring ended");
            }
        };
        const offT = twilioDevice.on(handle);
        const offP = plivoDevice.on(handle);
        return () => { offT(); offP(); };
    }, [stopHeartbeat]);

    // On a hard refresh / tab close the monitor WebRTC leg dies, but React's
    // unmount cleanup can't reliably finish an async monitor/stop. Use a beacon
    // so the backend drops this supervisor's listener entry immediately (instead
    // of waiting out the 15s heartbeat TTL) — keeps the "Monitoring" badge honest.
    useEffect(() => {
        const onHide = () => {
            const id = monitoredCallRef.current;
            if (id && navigator.sendBeacon) {
                navigator.sendBeacon(`${API_BASE}/calls/${id}/monitor/stop`);
            }
        };
        window.addEventListener("pagehide", onHide);
        return () => window.removeEventListener("pagehide", onHide);
    }, []);

    const startListen = async (call) => {
        try {
            if (!monDevice.isReady()) {
                if (monProvider === "plivo") await monDevice.ensureDevice();
                else await monDevice.ensureDevice(`agent_${user.id}`);
            }
            const { data } = await api.post(`/calls/${call.id}/monitor/start`);
            await monDevice.connectMonitor(data.room);
            setMonitoringId(call.id);
            monitoredCallRef.current = call.id;
            stopHeartbeat(false);
            heartbeatRef.current = setInterval(() => {
                api.post(`/calls/${call.id}/monitor/ping`).catch(() => {});
            }, 8000);
            toast.success("Listening in (muted)");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start monitoring");
        }
    };

    const stopListen = () => {
        monDevice.stopMonitor();
        stopHeartbeat();
        setMonitoringId(null);
        refreshLive();
    };

    return (
        <AppShell onCallCompleted={refresh}>
            {showOnboarding && (
                <OnboardingWizard orgName={user?.org_name} onClose={() => setShowOnboarding(false)} />
            )}
            <div className="mx-auto max-w-6xl px-6 py-8 md:px-12 md:py-12">
                {/* Header */}
                <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Overview</div>
                        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
                        <p className="mt-2 text-sm text-stone2">Your workspace at a glance{callCenter && waEnabled ? " across voice and messaging" : ""}.</p>
                    </div>
                    {callCenter && (
                        <Link
                            to="/calls"
                            data-testid="view-history-link"
                            className="group inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-ink"
                        >
                            View Call History
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    )}
                </div>

                {!callCenter && (
                    <div className="mb-10" data-testid="overview-calling-upsell">
                        <CallingUpsellCard variant="banner" />
                    </div>
                )}

                {callCenter && (<>
                {/* Stats */}
                <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {[
                        { label: "Total Calls", value: stats.total_calls, icon: PhoneOutgoing },
                        { label: "Completed", value: stats.completed, icon: PhoneIncoming },
                        { label: "Failed", value: stats.failed, icon: PhoneOutgoing },
                        { label: "Talk Time", value: fmtDur(stats.total_duration_sec), icon: Clock },
                    ].map((s) => (
                        <div key={s.label} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-stone2">{s.label}</span>
                                <s.icon size={16} className="text-stone-400" />
                            </div>
                            <div className="mt-3 font-heading text-3xl font-bold tabular">{s.value}</div>
                        </div>
                    ))}
                </div>

                {/* Live calls — supervisor board (admins) */}
                {isAdmin && liveCalls.length > 0 && (
                    <div className="mb-10" data-testid="live-calls-board">
                        <div className="mb-4 flex items-center gap-2">
                            <Broadcast size={16} weight="fill" className="text-emerald-500 animate-pulse" />
                            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Live calls · supervisor</div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {liveCalls.map((c) => {
                                const target = c.direction === "inbound" ? c.from_number : c.to_number;
                                const isMon = monitoringId === c.id;
                                return (
                                    <div key={c.id} data-testid={`live-call-${c.id}`} className="rounded-2xl border border-stone-200 bg-white p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-stone2">{c.direction}</span>
                                            <StatusPill status={c.status} />
                                        </div>
                                        <div className="mt-2 font-heading text-lg font-semibold tabular">{target || "—"}</div>
                                        <div className="text-xs text-stone2">Agent: {c.user_name || "—"}</div>
                                        {c.monitor_count > 0 && (
                                            <div
                                                data-testid={`monitoring-badge-${c.id}`}
                                                title={`Listening: ${(c.monitors || []).join(", ")}`}
                                                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600"
                                            >
                                                <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                                                Monitoring · {c.monitor_count}
                                            </div>
                                        )}
                                        <button
                                            data-testid={`listen-${c.id}`}
                                            onClick={() => (isMon ? stopListen() : startListen(c))}
                                            disabled={c.status !== "connected" || (monitoringId && !isMon)}
                                            title={c.status !== "connected" ? "Available once the agent answers (call connected)" : (isMon ? "Stop listening" : "Listen in (muted)")}
                                            className={`mt-3 flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all disabled:opacity-40 ${
                                                isMon ? "bg-red-500 text-white hover:bg-red-600" : "border border-stone-200 bg-white hover:border-ink"
                                            }`}
                                        >
                                            {isMon ? <><Stop size={15} weight="fill" /> Stop listening</> : <><Headphones size={15} weight="fill" /> {c.status === "connected" ? "Listen in" : "Waiting to connect"}</>}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Recent activity (click-to-call) */}
                <div>
                    <div className="mb-6 flex items-end justify-between">
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Latest Activity</div>
                            <h2 className="mt-1 font-heading text-2xl font-semibold">Recent calls</h2>
                        </div>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-stone-50/70">
                                    <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">When</th>
                                    <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Number</th>
                                    <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Direction</th>
                                    <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Status</th>
                                    <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Duration</th>
                                    <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.18em] text-stone2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent.length === 0 && (
                                    <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-stone2" data-testid="recent-empty">No calls yet. Hit the orange button to dial →</td></tr>
                                )}
                                {recent.map((c) => {
                                    const target = c.direction === "inbound" ? c.from_number : c.to_number;
                                    return (
                                        <tr key={c.id} className="group border-t border-stone-100 hover:bg-stone-50" data-testid={`recent-row-${c.id}`}>
                                            <td className="px-4 py-3 text-sm text-stone2">{fmtDate(c.created_at)}</td>
                                            <td className="px-4 py-3 font-heading text-sm font-medium tabular">{target || "—"}</td>
                                            <td className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-stone2">{c.direction}</td>
                                            <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                                            <td className="px-4 py-3 text-right tabular text-sm">{fmtDur(c.duration_sec)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    data-testid={`redial-${c.id}`}
                                                    onClick={() => twilioDevice.requestDial(target)}
                                                    disabled={!target}
                                                    className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold opacity-0 transition-opacity group-hover:opacity-100 hover:bg-brand hover:text-white disabled:opacity-0"
                                                    title="Redial"
                                                >
                                                    <Phone size={12} weight="fill" /> Call
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                </>)}

                {/* WhatsApp overview */}
                {waEnabled && (
                    <div className={callCenter ? "mt-12" : ""} data-testid="overview-whatsapp">
                        <div className="mb-5 flex items-end justify-between">
                            <div>
                                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-600">
                                    <WhatsappLogo size={15} weight="fill" /> Messaging
                                </div>
                                <h2 className="mt-1 font-heading text-2xl font-semibold">WhatsApp today</h2>
                            </div>
                            <Link to="/whatsapp" data-testid="overview-open-inbox"
                                className="group inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-emerald-500">
                                Open Inbox <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                            </Link>
                        </div>

                        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                            {[
                                { label: "Replies today", value: ov?.whatsapp?.in_today ?? 0, sub: `${ov?.whatsapp?.in_week ?? 0} this week`, icon: ChatCircleText },
                                { label: "Sent today", value: ov?.whatsapp?.out_today ?? 0, sub: `${ov?.whatsapp?.out_week ?? 0} this week`, icon: PaperPlaneTilt },
                                { label: "Active journeys", value: ov?.whatsapp?.active_journeys ?? 0, sub: "running now", icon: Broadcast },
                                { label: "Captured leads", value: ov?.whatsapp?.captured_week ?? 0, sub: "last 7 days", icon: UserPlus },
                            ].map((s) => (
                                <div key={s.label} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`wa-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">{s.label}</span>
                                        <s.icon size={16} className="text-emerald-500" />
                                    </div>
                                    <div className="mt-3 font-heading text-3xl font-bold tabular">{s.value}</div>
                                    <div className="mt-1 text-[11px] text-stone2">{s.sub}</div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 grid gap-4 lg:grid-cols-3">
                            {/* Recent broadcast performance */}
                            <div className="rounded-2xl border border-stone-200 bg-white p-5 lg:col-span-2" data-testid="wa-recent-broadcast">
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Latest broadcast</div>
                                {ov?.whatsapp?.recent_broadcast ? (
                                    <div className="mt-2">
                                        <div className="font-heading text-lg font-semibold">{ov.whatsapp.recent_broadcast.name}</div>
                                        <div className="mt-3 flex gap-8 text-center">
                                            <div><div className="font-heading text-2xl font-bold tabular text-sky-600">{ov.whatsapp.recent_broadcast.sent}</div><div className="text-xs text-stone2">sent</div></div>
                                            <div><div className="font-heading text-2xl font-bold tabular text-emerald-600">{ov.whatsapp.recent_broadcast.read}</div><div className="text-xs text-stone2">read</div></div>
                                            <div><div className="font-heading text-2xl font-bold tabular text-emerald-600">{ov.whatsapp.recent_broadcast.replied}</div><div className="text-xs text-stone2">replied</div></div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="mt-2 text-sm text-stone2">No broadcasts sent yet.</p>
                                )}
                            </div>
                            {/* Quick actions */}
                            <div className="flex flex-col gap-2">
                                <Link to="/whatsapp?tab=broadcasts" data-testid="wa-quick-broadcast"
                                    className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white px-5 py-4 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-emerald-500">
                                    <span className="flex items-center gap-2"><Broadcast size={16} className="text-emerald-500" /> New broadcast</span>
                                    <ArrowRight size={15} />
                                </Link>
                                <Link to="/whatsapp?tab=journeys" data-testid="wa-quick-journey"
                                    className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white px-5 py-4 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-emerald-500">
                                    <span className="flex items-center gap-2"><PaperPlaneTilt size={16} className="text-emerald-500" /> Build a journey</span>
                                    <ArrowRight size={15} />
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
